import { responseRejectInterceptor } from "@api/base";
import { literatureApi } from "@api/literature/literature";
import { userApi } from "@api/users/user";
import { searchHistoryApi } from "@api/search_history/search_history";
import { specialCollectionsApi } from "@api/specialCollections";
import { Media } from "@components/Media";
import {
  invenioConfig,
  setReactSearchKitInitialQueryState,
  setReactSearchKitDefaultSortingOnEmptyQueryString,
  setReactSearchKitUrlHandler,
} from "@config";
import history from "@history";
import { LiteratureSearchOverridesMap } from "@modules/Literature/LiteratureSearchOverrides";
import { SearchControls } from "@modules/SearchControls/SearchControls";
import { SearchControlsOverridesMap } from "@modules/SearchControls/SearchControlsOverrides";
import { ThesisSearchOverridesMap } from "@modules/SearchControls/ThesisSearchOverrides";
import SearchFooter from "@modules/SearchControls/SearchFooter";
import React, { Component } from "react";
import Overridable, { OverridableContext } from "react-overridable";
import {
  EmptyResults,
  Error,
  InvenioSearchApi,
  ReactSearchKit,
  ResultsLoader,
  ResultsMultiLayout,
  withState,
} from "react-searchkit";
import {
  Container,
  Grid,
  Header,
  Button,
  Icon,
  Transition,
  Segment,
  Dropdown,
} from "semantic-ui-react";
import Qs from "qs";
import { SearchBarOverridesMap } from "@components/SearchBar/SearchBarOverrides";
import PropTypes from "prop-types";
import LiteratureSearchMobile from "./LiteratureSearchMobile";
import SearchMessage from "./SearchMessage/SearchMessage";
import SearchGuideLink from "./SearchGuideLink/SearchGuideLink";
import SearchAggregation from "./SearchAggregation";
import { ExportReactSearchKitResults } from "@components/backoffice/ExportSearchResults";
import SessionFiltersPersistence from "@modules/SearchControls/SessionFiltersPersistence";
import ClearFiltersButton from "../../../../modules/SearchControls/ClearFiltersButton";
import { ReactSearchKitEnhancedSearchBar } from "@components/SearchBar/ReactSearchKitEnhancedSearchBar";
import AdvancedSearchForm from "./AdvancedSearchForm";
import CascadingDropdownFilter from "@modules/SearchControls/overridden/CascadingDropdownElement";
import ThesisSearchMobile from "./ThesisSearchMobile";
import { generateExportHTML } from "@components/HtmlExportTemplate/htmlExportTemplate";

const ROLES = {
  FACULTY: ["faculty", "admin"],
  ADMIN: ["admin", "administrator", "super_admin"],
  STAFF: ["librarian", "admin", "staff", "administrator", "super_admin"],
  PATRON: ["patron", "faculty"],
};

const SEARCH_MODES = {
  GENERAL: "general",
  THESIS: "thesis",
};

const MODEL_NAMES = {
  LITERATURE: "LITERATURE",
  SPECIAL_COLLECTIONS: "SPECIAL_COLLECTIONS",
};

const SEARCH_ESTIMATION_TIME = 1000;
const SUGGESTION_DEBOUNCE_TIME = 300;
const SUGGESTION_BLUR_DELAY = 150;
const MAX_SUGGESTIONS = 6;
const MAX_ATTACH_ATTEMPTS = 15;
const ATTACH_RETRY_DELAY = 250;

const SEARCH_MODE_OPTIONS = [
  { key: "general", text: "General Collection", value: "general" },
  { key: "thesis", text: "Theses and Dissertations", value: "thesis" },
];

const IDENTIFIER_PATTERNS = [
  /\b10\.\d{4,}\/\S+\b/, // DOI
  /^\d{9}[\dXx]$/, // ISBN-10
  /^97[89]\d{10}$/, // ISBN-13
  /^(97[89][-\s]?\d{1,5}[-\s]?\d+[-\s]?\d+[-\s]?[0-9Xx])$/, // ISBN with hyphens
  /[\d-]{5,}/, // Standard numbers
];

// UTILITY FUNCTIONS
const extractHitsFromResponse = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data.hits)) return data.hits;
  if (data.hits?.hits) return data.hits.hits.map((h) => h._source || h);
  if (data.data?.hits?.hits)
    return data.data.hits.hits.map((h) => h._source || h);
  if (data.data?.hits) return data.data.hits;
  if (data.results) return data.results;
  if (data.metadata?.hits) return data.metadata.hits;
  return [];
};

const extractTitle = (item) => {
  if (!item) return "";
  if (typeof item === "string") return item;

  return (
    item.title ||
    item.metadata?.title ||
    item.json?.title ||
    item.record?.title ||
    item._source?.title ||
    item.pid?.identifier ||
    item.id ||
    item.name ||
    ""
  );
};

const formatSuggestions = (hits, maxResults) => {
  return hits
    .map((hit) => {
      const title = extractTitle(hit);
      return title ? { label: title, raw: hit } : null;
    })
    .filter(Boolean)
    .slice(0, maxResults);
};

const escapeCSV = (field) => {
  const str = String(field || "");
  const needsQuoting =
    str.includes(",") ||
    str.includes('"') ||
    str.includes("\n") ||
    str.includes("\r") ||
    str.includes(";");

  if (needsQuoting) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
};

const escapeHtml = (text) => {
  const div = document.createElement("div");
  div.textContent = text;
  return div.innerHTML;
};

class SearchSuggestions extends Component {
  static propTypes = {
    query: PropTypes.string,
    onSelect: PropTypes.func.isRequired,
    max: PropTypes.number,
    isVisible: PropTypes.bool,
  };

  static defaultProps = {
    query: "",
    max: MAX_SUGGESTIONS,
    isVisible: true,
  };

  constructor(props) {
    super(props);
    this.state = {
      suggestions: [],
      loading: false,
      error: null,
      selectedIndex: -1,
    };
    this.abortControllerRef = null;
  }

  componentDidMount() {
    if (this.props.isVisible) {
      document.addEventListener("keydown", this.handleKeyDown);
    }
  }

  componentDidUpdate(prevProps) {
    const { query, isVisible, max } = this.props;

    // Handle visibility changes
    if (prevProps.isVisible !== isVisible) {
      if (isVisible) {
        document.addEventListener("keydown", this.handleKeyDown);
      } else {
        document.removeEventListener("keydown", this.handleKeyDown);
      }
    }

    // Handle query changes
    if (prevProps.query !== query || prevProps.max !== max) {
      this.fetchSuggestions();
    }
  }

  componentWillUnmount() {
    document.removeEventListener("keydown", this.handleKeyDown);
    if (this.abortControllerRef) {
      this.abortControllerRef.abort();
    }
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
    }
  }

  fetchSuggestions = () => {
    const { query, max, isVisible } = this.props;

    if (!query?.trim() || !isVisible) {
      this.setState({
        suggestions: [],
        loading: false,
        error: null,
        selectedIndex: -1,
      });
      return;
    }

    // Cancel previous request
    if (this.abortControllerRef) {
      this.abortControllerRef.abort();
    }

    this.abortControllerRef = new AbortController();
    this.setState({ loading: true, error: null, selectedIndex: -1 });

    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
    }

    this.timeoutId = setTimeout(async () => {
      try {
        const trimmedQuery = query.trim();
        const encodedQuery = encodeURIComponent(trimmedQuery);
        const url = `${literatureApi.searchBaseURL}?q=${encodedQuery}&size=${max}`;

        const response = await fetch(url, {
          credentials: "include",
          signal: this.abortControllerRef?.signal,
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const data = await response.json();
        const hits = extractHitsFromResponse(data);
        const formattedSuggestions = formatSuggestions(hits, max);

        this.setState({ suggestions: formattedSuggestions, loading: false });
      } catch (err) {
        if (err.name !== "AbortError") {
          this.setState({
            error: err.message || "Error fetching suggestions",
            suggestions: [],
            loading: false,
          });
        }
      }
    }, SUGGESTION_DEBOUNCE_TIME);
  };

  handleKeyDown = (e) => {
    const { suggestions, selectedIndex } = this.state;
    const { onSelect } = this.props;

    if (!suggestions.length) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        this.setState({
          selectedIndex:
            selectedIndex < suggestions.length - 1 ? selectedIndex + 1 : 0,
        });
        break;
      case "ArrowUp":
        e.preventDefault();
        this.setState({
          selectedIndex:
            selectedIndex > 0 ? selectedIndex - 1 : suggestions.length - 1,
        });
        break;
      case "Enter":
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
          onSelect(suggestions[selectedIndex].label);
        }
        break;
      case "Escape":
        this.setState({ selectedIndex: -1 });
        break;
      default:
        break;
    }
  };

  renderSuggestion = (suggestion, index) => {
    const { selectedIndex } = this.state;
    const { onSelect } = this.props;
    const isSelected = index === selectedIndex;

    return (
      <li
        key={`${suggestion.label}-${index}`}
        role="option"
        aria-selected={isSelected}
        onMouseDown={(e) => e.preventDefault()}
        onMouseEnter={() => this.setState({ selectedIndex: index })}
        onClick={() => onSelect(suggestion.label)}
        tabIndex={0}
        style={{
          padding: "0.75rem",
          cursor: "pointer",
          backgroundColor: isSelected ? "#f5f5f5" : "transparent",
          borderTop: index > 0 ? "1px solid rgba(0,0,0,0.05)" : "none",
          transition: "background-color 0.2s ease",
        }}
      >
        <div
          style={{ display: "flex", alignItems: "flex-start", gap: "0.75rem" }}
        >
          <img
            src={
              suggestion.raw.metadata?.cover_metadata?.urls?.small ||
              suggestion.raw.metadata?.cover_metadata?.urls?.medium ||
              "/default-thumbnail.png"
            }
            alt={suggestion.label}
            style={{
              width: 30,
              height: 50,
              objectFit: "cover",
              borderRadius: 4,
              flexShrink: 0,
              background: "#f0f0f0",
            }}
          />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                fontSize: 14,
                fontWeight: isSelected ? 600 : 500,
                color: "#333",
                marginBottom: "0.25rem",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {suggestion.raw.metadata?.title || suggestion.label}
            </div>
            <div
              style={{
                fontSize: 12,
                color: "#666",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {(() => {
                const metadata = suggestion.raw.metadata || {};
                const authors = metadata.authors || [];
                const otherAuthors = metadata.other_authors || [];
                const firstAuthor = authors[0] || otherAuthors[0];
                return firstAuthor?.full_name || "";
              })()}
            </div>
          </div>
        </div>
      </li>
    );
  };

  render() {
    const { query, isVisible } = this.props;
    const { suggestions, loading, error } = this.state;

    if (!query?.trim() || !isVisible) return null;

    return (
      <div
        role="listbox"
        aria-label="Search suggestions"
        style={{
          marginTop: "0.4rem",
          border: "1px solid rgba(0,0,0,0.1)",
          borderRadius: 6,
          background: "#fff",
          boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
          width: "100%",
          zIndex: 1,
          position: "absolute",
          left: 0,
          right: 0,
        }}
      >
        <div
          style={{
            padding: "0.75rem",
            fontSize: 12,
            color: "#666",
            borderBottom:
              suggestions.length > 0 ? "1px solid rgba(0,0,0,0.05)" : "none",
          }}
        >
          {loading ? (
            <span>🔍 Searching suggestions...</span>
          ) : error ? (
            <span style={{ color: "#d32f2f" }}>⚠️ Error: {error}</span>
          ) : (
            <span>💡 Suggestions</span>
          )}
        </div>

        {!loading && suggestions.length === 0 && !error && (
          <div
            style={{
              padding: "1rem 0.75rem",
              color: "#999",
              fontStyle: "italic",
              textAlign: "center",
            }}
          >
            No suggestions found
          </div>
        )}

        {!loading && suggestions.length > 0 && (
          <ul
            style={{
              listStyle: "none",
              margin: 0,
              padding: "0.25rem 0",
              maxHeight: "300px",
              overflowY: "auto",
            }}
          >
            {suggestions.map((suggestion, index) =>
              this.renderSuggestion(suggestion, index)
            )}
          </ul>
        )}
      </div>
    );
  }
}

class EnhancedSearchBarComponent extends Component {
  static displayName = "EnhancedSearchBar";

  static propTypes = {
    updateQueryState: PropTypes.func.isRequired,
    currentQueryState: PropTypes.shape({
      queryString: PropTypes.string,
      filters: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
      sortBy: PropTypes.string,
    }),
    currentResultsState: PropTypes.shape({
      data: PropTypes.shape({
        total: PropTypes.number,
        hits: PropTypes.array,
      }),
      loading: PropTypes.bool,
      error: PropTypes.object,
    }),
    onForceRefresh: PropTypes.func,
  };

  static defaultProps = {
    currentQueryState: null,
    currentResultsState: null,
    onForceRefresh: null,
  };

  constructor(props) {
    super(props);
    this.state = {
      isTrackingEnabled: false,
      lastTrackedQuery: null,
      searchStartTime: null,
      isInitialized: false,
      liveQuery: "",
      showSuggestions: false,
      inputHasFocus: false,
    };

    this.searchBarContainerRef = React.createRef();
    this.attachedInput = null;
    this.attachAttempts = 0;
    this.observer = null;
    this.searchTimeoutId = null;
    this._isMounted = false;
    this.blurTimeoutId = null;
    this.updateTimeoutIds = [];
  }

  componentDidMount() {
    this._isMounted = true;
    this.initializeSearchHistoryApi();
    this.tryAttachInputListener();
    document.addEventListener("click", this.handleDocumentClick);
  }

  componentWillUnmount() {
    this._isMounted = false;
    this.detachInputListener();
    document.removeEventListener("click", this.handleDocumentClick);

    if (this.searchTimeoutId) {
      clearTimeout(this.searchTimeoutId);
      this.searchTimeoutId = null;
    }
    if (this.blurTimeoutId) {
      clearTimeout(this.blurTimeoutId);
      this.blurTimeoutId = null;
    }

    this.updateTimeoutIds.forEach((id) => clearTimeout(id));
    this.updateTimeoutIds = [];
  }

  componentDidUpdate(prevProps) {
    if (!this._isMounted) return;

    const { currentQueryState, currentResultsState } = this.props;
    const { isTrackingEnabled, lastTrackedQuery, inputHasFocus } = this.state;

    if (isTrackingEnabled) {
      const prevLoading = prevProps.currentResultsState?.loading;
      const currentLoading = currentResultsState?.loading;
      const currentQuery = currentQueryState?.queryString?.trim();

      if (
        prevLoading &&
        !currentLoading &&
        currentQuery &&
        currentQuery !== lastTrackedQuery
      ) {
        this.trackSearchCompletion();
      }

      const prevQuery = prevProps.currentQueryState?.queryString?.trim();
      if (
        currentQuery &&
        currentQuery !== prevQuery &&
        !this.state.searchStartTime
      ) {
        const timeoutId = setTimeout(() => {
          if (this._isMounted) {
            this.setState({ searchStartTime: Date.now() });
          }
        }, 0);
        this.updateTimeoutIds.push(timeoutId);
      }

      if (!prevLoading && currentLoading && currentQuery) {
        const timeoutId = setTimeout(() => {
          if (this._isMounted) {
            this.setState({ searchStartTime: Date.now() });
          }
        }, 0);
        this.updateTimeoutIds.push(timeoutId);
      }
    }

    // Sync liveQuery with external changes
    const prevQueryString = prevProps.currentQueryState?.queryString || "";
    const currentQueryString = currentQueryState?.queryString || "";

    if (prevQueryString !== currentQueryString && !inputHasFocus) {
      const timeoutId = setTimeout(() => {
        if (this._isMounted) {
          this.setState({ liveQuery: currentQueryString });
        }
      }, 0);
      this.updateTimeoutIds.push(timeoutId);
    }
  }

  initializeSearchHistoryApi = async () => {
    try {
      if (searchHistoryApi?.testConnection) {
        await searchHistoryApi.testConnection();
        if (this._isMounted) {
          this.setState({ isTrackingEnabled: true, isInitialized: true });
        }
      } else {
        if (this._isMounted) {
          this.setState({ isTrackingEnabled: false, isInitialized: true });
        }
      }
    } catch (error) {
      if (this._isMounted) {
        this.setState({ isTrackingEnabled: false, isInitialized: true });
      }
    }
  };

  handleDocumentClick = (e) => {
    if (!this._isMounted) return;

    const container = this.searchBarContainerRef?.current;
    if (container && !container.contains(e.target)) {
      this.setState({ showSuggestions: false, inputHasFocus: false });
    }
  };

  tryAttachInputListener = () => {
    if (!this._isMounted) return;

    this.attachAttempts++;

    const container = this.searchBarContainerRef?.current;
    if (!container && this.attachAttempts < MAX_ATTACH_ATTEMPTS) {
      setTimeout(this.tryAttachInputListener, ATTACH_RETRY_DELAY);
      return;
    }

    const inputSelectors = [
      'input[type="search"]',
      'input[name="q"]',
      'input[role="search"]',
      'input[aria-label*="search" i]',
      'input[placeholder*="search" i]',
      ".ui.search input",
      ".search-bar input",
      "input",
    ];

    let input = null;
    for (const selector of inputSelectors) {
      input = container?.querySelector(selector);
      if (input) break;
    }

    if (input && input !== this.attachedInput) {
      this.detachInputListener();
      this.attachedInput = input;

      input.addEventListener("input", this.onInternalInput);
      input.addEventListener("focus", this.onInputFocus);
      input.addEventListener("blur", this.onInputBlur);
      input.addEventListener("keydown", this.onInputKeyDown);

      if (this._isMounted) {
        this.setState({
          liveQuery: input.value || "",
          showSuggestions: document.activeElement === input,
        });
      }

      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
      return;
    }

    // Setup mutation observer as fallback
    if (this.attachAttempts < MAX_ATTACH_ATTEMPTS) {
      if (!this.observer && typeof MutationObserver !== "undefined") {
        try {
          this.observer = new MutationObserver(() => {
            if (this._isMounted) {
              this.tryAttachInputListener();
            }
          });
          this.observer.observe(container, { childList: true, subtree: true });
        } catch (err) {
          setTimeout(this.tryAttachInputListener, ATTACH_RETRY_DELAY);
        }
      } else {
        setTimeout(this.tryAttachInputListener, ATTACH_RETRY_DELAY);
      }
    }
  };

  detachInputListener = () => {
    if (this.attachedInput) {
      try {
        this.attachedInput.removeEventListener("input", this.onInternalInput);
        this.attachedInput.removeEventListener("focus", this.onInputFocus);
        this.attachedInput.removeEventListener("blur", this.onInputBlur);
        this.attachedInput.removeEventListener("keydown", this.onInputKeyDown);
        this.attachedInput = null;
      } catch (err) {
        // Silent fail
      }
    }

    if (this.observer) {
      try {
        this.observer.disconnect();
        this.observer = null;
      } catch (err) {
        // Silent fail
      }
    }
  };

  onInternalInput = (e) => {
    if (!this._isMounted) return;

    const value = e?.target?.value ?? "";
    this.setState({
      liveQuery: value,
      showSuggestions: value.trim().length > 0,
    });
  };

  onInputFocus = () => {
    if (!this._isMounted) return;

    const { liveQuery } = this.state;
    this.setState({
      inputHasFocus: true,
      showSuggestions: liveQuery.trim().length > 0,
    });
  };

  onInputBlur = () => {
    if (this.blurTimeoutId) {
      clearTimeout(this.blurTimeoutId);
      this.blurTimeoutId = null;
    }

    this.blurTimeoutId = setTimeout(() => {
      if (this._isMounted) {
        this.setState({ inputHasFocus: false, showSuggestions: false });
      }
      this.blurTimeoutId = null;
    }, SUGGESTION_BLUR_DELAY);
  };

  onInputKeyDown = (e) => {
    const { showSuggestions } = this.state;
    if (showSuggestions && ["ArrowDown", "ArrowUp", "Enter"].includes(e.key)) {
      return; // Let SearchSuggestions handle these
    }
  };

  trackSearchCompletion = async () => {
    if (!this._isMounted) return;

    const { currentQueryState, currentResultsState } = this.props;
    const { searchStartTime } = this.state;

    const actualStartTime =
      searchStartTime || Date.now() - SEARCH_ESTIMATION_TIME;

    try {
      const query = currentQueryState?.queryString?.trim();
      const results = currentResultsState?.data;
      const filters = currentQueryState?.filters || {};
      const sort = currentQueryState?.sortBy;
      const error = currentResultsState?.error;

      if (!query) return;

      const searchMetadata = {
        total: results?.total || 0,
        hits: results?.hits || [],
        error: error?.message || null,
        hasResults: (results?.total || 0) > 0,
      };

      const filterMetadata = { filters, sort, scope: "literature" };

      await searchHistoryApi.trackSearchPerformance(
        query,
        actualStartTime,
        searchMetadata,
        filterMetadata
      );

      const timeoutId = setTimeout(() => {
        if (this._isMounted) {
          this.setState({ lastTrackedQuery: query, searchStartTime: null });
        }
      }, 0);
      this.updateTimeoutIds.push(timeoutId);
    } catch (error) {
      const timeoutId = setTimeout(() => {
        if (this._isMounted) {
          this.setState({ searchStartTime: null });
        }
      }, 0);
      this.updateTimeoutIds.push(timeoutId);
    }
  };

  handleRepeatSearch = (query) => {
    if (!this._isMounted) return;

    this.setState({
      searchStartTime: Date.now(),
      liveQuery: query,
      showSuggestions: false,
    });

    this.props.updateQueryState({
      queryString: query,
      filters: {},
      sortBy: undefined,
      page: 1,
      p: 1,
    });
  };

  handleSearchSubmit = (query) => {
    if (!this._isMounted) return;

    this.setState({
      searchStartTime: Date.now(),
      liveQuery: query,
      showSuggestions: false,
    });

    this.props.updateQueryState({
      queryString: query,
      page: 1,
      p: 1,
    });
  };

  buildQueryFromSuggestion = (label) => {
    if (!label) return "";

    const trimmed = String(label).trim();
    if (!trimmed) return "";

    // Check for advanced syntax
    const hasAdvancedSyntax =
      /[:"()]/.test(trimmed) || /\b(AND|OR|NOT|\+|-)\b/i.test(trimmed);
    if (hasAdvancedSyntax) return trimmed;

    // Check if it's an identifier
    const isIdentifier = IDENTIFIER_PATTERNS.some((pattern) =>
      pattern.test(trimmed)
    );
    if (isIdentifier) {
      return `"${trimmed.replace(/"/g, '\\"')}"`;
    }

    // Build multi-field query
    const escaped = trimmed.replace(/"/g, '\\"');
    return `(title:"${escaped}")`;
  };

  handleSuggestionSelect = (suggestionLabel) => {
    const builtQuery = this.buildQueryFromSuggestion(suggestionLabel);

    this.setState({
      showSuggestions: false,
      liveQuery: builtQuery,
      searchStartTime: Date.now(),
    });

    // Update input field
    if (this.attachedInput) {
      try {
        this.attachedInput.value = builtQuery;
        this.attachedInput.focus();

        ["input", "change"].forEach((eventType) => {
          this.attachedInput.dispatchEvent(
            new Event(eventType, { bubbles: true, cancelable: true })
          );
        });
      } catch (err) {
        // Silent fallback
      }
    }

    if (this.searchTimeoutId) {
      clearTimeout(this.searchTimeoutId);
    }

    // Execute search
    try {
      this.props.updateQueryState({
        queryString: builtQuery,
        page: 1,
        p: 1,
        size: 15,
        s: 15,
        refresh: Date.now(),
      });

      setTimeout(() => {
        try {
          const searchParams = new URLSearchParams();
          searchParams.set("q", builtQuery);
          searchParams.set("p", "1");
          searchParams.set("s", "15");

          const newSearch = searchParams.toString();

          if (history?.push) {
            history.push({
              pathname: window.location.pathname,
              search: newSearch,
            });
          } else if (history?.replace) {
            history.replace({
              pathname: window.location.pathname,
              search: newSearch,
            });
          } else {
            const newUrl = `${window.location.pathname}?${newSearch}`;
            window.history.pushState(null, "", newUrl);
            window.dispatchEvent(new Event("hashchange"));
            window.dispatchEvent(new PopStateEvent("popstate"));
          }

          if (this.props.onForceRefresh) {
            setTimeout(() => {
              this.props.onForceRefresh();
            }, 100);
          }
        } catch (urlErr) {
          console.warn("URL update failed:", urlErr);
          const encodedQuery = encodeURIComponent(builtQuery);
          window.location.href = `${window.location.pathname}?q=${encodedQuery}&p=1&s=15`;
        }
      }, 50);
    } catch (err) {
      console.error("Error executing suggestion search:", err);
      setTimeout(() => {
        const encodedQuery = encodeURIComponent(builtQuery);
        window.location.href = `${window.location.pathname}?q=${encodedQuery}&p=1&s=15`;
      }, 100);
    }
  };

  render() {
    const {
      updateQueryState,
      currentQueryState,
      currentResultsState,
      ...otherProps
    } = this.props;
    const {
      isTrackingEnabled,
      isInitialized,
      liveQuery,
      showSuggestions,
      inputHasFocus,
    } = this.state;

    if (!isInitialized) {
      return (
        <div style={{ padding: "1rem", textAlign: "center" }}>
          <div>Initializing search...</div>
        </div>
      );
    }

    const suggestionQuery = liveQuery || currentQueryState?.queryString || "";
    const shouldShowSuggestions =
      showSuggestions && (inputHasFocus || liveQuery.trim().length > 0);

    return (
      <div ref={this.searchBarContainerRef} style={{ position: "relative" }}>
        <ReactSearchKitEnhancedSearchBar
          {...otherProps}
          onRepeatSearch={this.handleRepeatSearch}
          onSearchSubmit={this.handleSearchSubmit}
          currentQuery={currentQueryState?.queryString || ""}
          isLoading={currentResultsState?.loading || false}
          searchHistoryEnabled={isTrackingEnabled}
          searchHistoryApi={searchHistoryApi}
        />
        <SearchSuggestions
          query={suggestionQuery}
          onSelect={this.handleSuggestionSelect}
          max={MAX_SUGGESTIONS}
          isVisible={shouldShowSuggestions}
        />
      </div>
    );
  }
}

const EnhancedSearchBar = withState(EnhancedSearchBarComponent);

class ExportManager {
  constructor(component) {
    this.component = component;
  }

  getRequestSerializer() {
    const searchApi = this.component.getSearchApi();
    const requestSerializer = searchApi.requestSerializer;

    if (
      !requestSerializer ||
      typeof requestSerializer.serialize !== "function"
    ) {
      throw new Error("requestSerializer is not configured correctly");
    }

    return requestSerializer;
  }

  serializeQueryState(currentQueryState, size) {
    const requestSerializer = this.getRequestSerializer();
    const queryString = requestSerializer.serialize({
      ...currentQueryState,
      size,
      page: 1,
    });
    return Qs.parse(queryString);
  }

  buildExportUrl(baseUrl, params, format) {
    params["page"] = 1;
    params["format"] = format;
    return `${baseUrl}?${Qs.stringify(params, {
      arrayFormat: "repeat",
      encode: false,
    })}`;
  }

  async fetchExportData(url) {
    const response = await fetch(url, { credentials: "include" });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return response;
  }

  async exportCSV(currentQueryState, size, baseUrl, columns, filename) {
    try {
      const params = this.serializeQueryState(currentQueryState, size);
      const exportUrl = this.buildExportUrl(baseUrl, params, "json");
      const response = await this.fetchExportData(exportUrl);
      const data = await response.json();
      const hits = data.hits?.hits || [];

      const headers = columns.map((col) => col.header);
      const csvRows = [headers.join(",")];

      hits.forEach((hit) => {
        const metadata = hit.metadata || {};
        const row = columns
          .map((col) => {
            const value = col.extract(metadata);
            return escapeCSV(value);
          })
          .join(",");
        csvRows.push(row);
      });

      const csvContent = csvRows.join("\n");
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);

      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `${filename}_${new Date().toISOString().split("T")[0]}.csv`
      );
      link.style.visibility = "hidden";

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("CSV Export Error:", error);
      alert(`CSV export failed: ${error.message}`);
    }
  }

  async exportPDF(
    currentQueryState,
    size,
    baseUrl,
    columns,
    reportTitle,
    reportType
  ) {
    try {
      const params = this.serializeQueryState(currentQueryState, size);
      const exportUrl = this.buildExportUrl(baseUrl, params, "json");
      const response = await this.fetchExportData(exportUrl);
      const jsonData = await response.json();
      const hits = jsonData.hits?.hits || [];

      const tableHeaders = columns
        .map((col) => `<th>${col.header}</th>`)
        .join("");

      const tableRows = hits
        .map((hit) => {
          const metadata = hit.metadata || {};
          const cells = columns
            .map((col) => {
              const value = col.extract(metadata);
              return `<td>${escapeHtml(value)}</td>`;
            })
            .join("");
          return `<tr>${cells}</tr>`;
        })
        .join("");

      const htmlContent = generateExportHTML({
        reportTitle,
        generatedDate: new Date().toLocaleString(),
        tableHeaders,
        tableRows,
        footerText: "Library Resource Center",
        reportType,
      });

      const blob = new Blob([htmlContent], { type: "text/html;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const printWindow = window.open(url, "_blank");

      setTimeout(() => URL.revokeObjectURL(url), 2000);

      if (printWindow) {
        printWindow.onload = () => {
          setTimeout(() => printWindow.focus(), 500);
        };
      }
    } catch (error) {
      console.error("PDF Export Error:", error);
      alert(`PDF export failed: ${error.message}`);
    }
  }

  exportGeneric(currentQueryState, size, baseUrl, format) {
    try {
      const params = this.serializeQueryState(currentQueryState, size);
      const exportUrl = this.buildExportUrl(baseUrl, params, format);
      window.open(exportUrl, "_blank");
    } catch (error) {
      console.error("Export Error:", error);
      alert(`Export failed: ${error.message}`);
    }
  }
}

const GENERAL_EXPORT_COLUMNS = [
  {
    header: "Title",
    extract: (metadata) => metadata.title || "",
  },
  {
    header: "Author",
    extract: (metadata) =>
      (metadata.authors || []).map((a) => a.full_name || "").join("; ") || "",
  },
  {
    header: "Published Year",
    extract: (metadata) => metadata.publication_year || "",
  },
  {
    header: "Material Type",
    extract: (metadata) => metadata.document_type || "",
  },
];

const THESIS_EXPORT_COLUMNS = [
  {
    header: "Title",
    extract: (metadata) => metadata.title || "",
  },
  {
    header: "Author(s)",
    extract: (metadata) =>
      (metadata.authors || []).map((a) => a.full_name || "").join("; ") || "",
  },
  {
    header: "Publication Year",
    extract: (metadata) => metadata.publication_year || "",
  },
  {
    header: "Collection Type",
    extract: (metadata) => metadata.collection_type || "",
  },
  {
    header: "Department",
    extract: (metadata) => metadata.academic_info?.department || "",
  },
  {
    header: "Program",
    extract: (metadata) => metadata.academic_info?.program || "",
  },
];

class LiteratureSearch extends Component {
  static propTypes = {
    overriddenSearchAppCmps: PropTypes.object,
    user: PropTypes.object,
    isLoadingUser: PropTypes.bool,
  };

  static defaultProps = {
    overriddenSearchAppCmps: null,
    user: null,
    isLoadingUser: false,
  };

  constructor(props) {
    super(props);

    const urlParams = new URLSearchParams(window.location.search);
    const mode = urlParams.get("mode");

    const validMode = Object.values(SEARCH_MODES).includes(mode)
      ? mode
      : SEARCH_MODES.GENERAL;

    this.state = {
      currentUser: null,
      isLoadingUser: true,
      hasError: false,
      errorMessage: null,
      refreshKey: 0,
      showAdvancedSearch: false,
      searchMode: validMode,
    };

    this.exportManager = new ExportManager(this);
  }

  componentDidMount() {
    this.initializeUserData();
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, errorMessage: error.message };
  }

  componentDidCatch(error, errorInfo) {
    console.error("LiteratureSearch Error:", error, errorInfo);
  }

  initializeUserData = async () => {
    try {
      const { user } = this.props;
      if (!user) {
        await this.fetchCurrentUser();
      } else {
        this.setState({ isLoadingUser: false });
      }
    } catch (error) {
      this.setState({ hasError: true, errorMessage: error.message });
    }
  };

  fetchCurrentUser = async () => {
    try {
      const response = await userApi.get();
      const userData = response.data;
      this.setState({ currentUser: userData, isLoadingUser: false });
    } catch (error) {
      this.setState({ currentUser: null, isLoadingUser: false });
    }
  };

  extractRoles = (entity) => {
    if (!entity?.user_types || !Array.isArray(entity.user_types)) {
      return [];
    }

    return entity.user_types
      .map((userType) => {
        if (typeof userType === "object" && userType.user_type) {
          return userType.user_type.toLowerCase();
        }
        if (typeof userType === "string") {
          return userType.toLowerCase();
        }
        return "";
      })
      .filter(Boolean);
  };

  hasAnyRole = (userRoles, targetRoles) => {
    return userRoles.some((role) => targetRoles.includes(role));
  };

  isCurrentUserPatronOrFaculty = () => {
    const { user } = this.props;
    const { currentUser } = this.state;
    const userData = user || currentUser;
    const userRoles = this.extractRoles(userData);

    return (
      this.hasAnyRole(userRoles, ROLES.PATRON) ||
      this.hasAnyRole(userRoles, ROLES.FACULTY)
    );
  };

  handleSearchModeToggle = (mode) => {
    this.setState((prevState) => ({
      searchMode: mode,
      refreshKey: prevState.refreshKey + 1,
    }));
  };

  handleForceRefresh = () => {
    this.setState((prevState) => ({
      refreshKey: prevState.refreshKey + 1,
    }));
  };

  getSearchApi = () => {
    const { searchMode } = this.state;
    const baseURL =
      searchMode === SEARCH_MODES.THESIS
        ? specialCollectionsApi.searchBaseURL
        : literatureApi.searchBaseURL;

    return new InvenioSearchApi({
      axios: { url: baseURL, withCredentials: true },
      interceptors: { response: { reject: responseRejectInterceptor } },
    });
  };

  handleExportClick = async (format, size, currentQueryState) => {
    const { searchMode } = this.state;

    if (searchMode === SEARCH_MODES.THESIS) {
      return this.handleThesisExport(format, size, currentQueryState);
    }
    return this.handleGeneralExport(format, size, currentQueryState);
  };

  handleGeneralExport = async (format, size, currentQueryState) => {
    if (format === "pdf") {
      return this.exportManager.exportPDF(
        currentQueryState,
        size,
        literatureApi.searchBaseURL,
        GENERAL_EXPORT_COLUMNS,
        "General Collection Export Report",
        "default"
      );
    }

    if (format === "csv") {
      return this.exportManager.exportCSV(
        currentQueryState,
        size,
        literatureApi.searchBaseURL,
        GENERAL_EXPORT_COLUMNS,
        "general_collection"
      );
    }

    if (format === "marc21") {
      return this.handleMARC21Export(currentQueryState, size, format);
    }

    return this.exportManager.exportGeneric(
      currentQueryState,
      size,
      literatureApi.searchBaseURL,
      format
    );
  };

  handleMARC21Export = async (currentQueryState, size, format) => {
    try {
      const requestSerializer = this.exportManager.getRequestSerializer();
      const queryString = requestSerializer.serialize({
        ...currentQueryState,
        size,
      });
      const params = Qs.parse(queryString);
      params["page"] = 1;
      params["format"] = format;

      const exportUrl = `${literatureApi.searchBaseURL}?${Qs.stringify(params, {
        arrayFormat: "repeat",
        encode: false,
      })}`;

      const response = await fetch(exportUrl, {
        credentials: "include",
        headers: {
          Accept:
            format === "marcxml"
              ? "application/marcxml+xml"
              : "application/marc",
        },
      });

      if (!response.ok) {
        const errorMsg = `Export failed: ${response.status} ${response.statusText}`;
        throw { message: errorMsg };
      }

      const blob = await response.blob();
      const fileName = this.generateFileName(format, size);

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      alert(`Export failed: ${error.message}`);
    }
  };

  // Generate filenames
  generateFileName = (format, size) => {
    const timestamp = new Date()
      .toISOString()
      .slice(0, 19)
      .replace(/[:-]/g, "");
    const sizeStr = size === -1 ? "all" : size.toString();

    switch (format) {
      case "marc21":
        return `literature_export_${sizeStr}_${timestamp}.mrc`;
      case "csv":
        return `literature_export_${sizeStr}_${timestamp}.csv`;
      case "pdf":
        return `literature_export_${sizeStr}_${timestamp}.pdf`;
      default:
        return `literature_export_${sizeStr}_${timestamp}.${format}`;
    }
  };

  handleThesisExport = async (format, size, currentQueryState) => {
    if (format === "pdf") {
      return this.exportManager.exportPDF(
        currentQueryState,
        size,
        specialCollectionsApi.searchBaseURL,
        THESIS_EXPORT_COLUMNS,
        "Thesis and Dissertations Export Report",
        "thesis"
      );
    }

    if (format === "csv") {
      return this.exportManager.exportCSV(
        currentQueryState,
        size,
        specialCollectionsApi.searchBaseURL,
        THESIS_EXPORT_COLUMNS,
        "thesis_dissertations"
      );
    }

    return this.exportManager.exportGeneric(
      currentQueryState,
      size,
      specialCollectionsApi.searchBaseURL,
      format
    );
  };

  toggleAdvancedSearch = () => {
    this.setState((prevState) => ({
      showAdvancedSearch: !prevState.showAdvancedSearch,
    }));
  };

  renderAdvancedSearchToggle = () => {
    const { showAdvancedSearch, searchMode } = this.state;

    return (
      <div style={{ marginTop: "10px", marginBottom: "10px" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "10px",
          }}
        >
          <Button
            basic
            size="small"
            onClick={this.toggleAdvancedSearch}
            icon
            labelPosition="left"
            style={{
              borderColor: "#2185d0",
              color: "#2185d0",
              backgroundColor: "transparent",
            }}
          >
            <Icon name={showAdvancedSearch ? "angle up" : "angle down"} />
            {showAdvancedSearch ? "Hide Advanced Search" : "Advanced Search"}
          </Button>

          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <span
              style={{ fontSize: "13px", color: "#666", fontWeight: "500" }}
            >
              Searching for
            </span>
            <Dropdown
              selection
              compact
              options={SEARCH_MODE_OPTIONS}
              value={searchMode}
              onChange={(e, { value }) => this.handleSearchModeToggle(value)}
              style={{
                minWidth: "200px",
                backgroundColor: "transparent",
                fontSize: "13px",
                color: "#666",
                borderColor: "transparent",
              }}
              className="search-mode-dropdown"
            />
          </div>
        </div>

        <Transition
          visible={showAdvancedSearch}
          animation="slide down"
          duration={300}
        >
          <Segment
            basic
            style={{
              marginTop: "15px",
              padding: "20px",
              backgroundColor: "#f8f9fa",
              border: "1px solid #e9ecef",
              borderRadius: "6px",
            }}
          >
            <AdvancedSearchForm />
          </Segment>
        </Transition>
      </div>
    );
  };

  renderSearchMessage = () => {
    const { isLoadingUser: propIsLoadingUser } = this.props;
    const { isLoadingUser: stateIsLoadingUser } = this.state;

    const isLoading = propIsLoadingUser || stateIsLoadingUser;

    if (isLoading) return null;

    if (this.isCurrentUserPatronOrFaculty()) {
      return (
        <Container className="search-results-message">
          <SearchMessage />
        </Container>
      );
    }
    return null;
  };

  renderDesktopView = (
    modelName,
    initialState,
    defaultSortingOnEmptyQueryString
  ) => {
    const { searchMode } = this.state;

    return (
      <Container fluid className="fs-search-body">
        <Grid columns={2} stackable relaxed className="grid-documents-search">
          <ResultsLoader>
            <Grid.Column
              data-tour="search-filters"
              width={3}
              className="search-aggregations"
            >
              <div className="search-clear-filters">
                <Header content="Filter by" />
                <ClearFiltersButton />
              </div>
              {searchMode === SEARCH_MODES.THESIS && (
                <div style={{ marginBottom: "1em" }}>
                  <CascadingDropdownFilter />
                </div>
              )}
              <SearchAggregation modelName={modelName} />
            </Grid.Column>

            <Grid.Column width={13} className="search-results">
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "1em",
                  flexWrap: "wrap",
                }}
              >
                <Header as="h3" style={{ margin: 0 }}>
                  {searchMode === SEARCH_MODES.THESIS
                    ? "Theses and Dissertations"
                    : "General Collection"}
                </Header>
                <ExportReactSearchKitResults
                  exportBaseUrl={
                    searchMode === SEARCH_MODES.THESIS
                      ? specialCollectionsApi.searchBaseURL
                      : literatureApi.searchBaseURL
                  }
                  onExportClick={this.handleExportClick}
                  supportedFormats={
                    searchMode === SEARCH_MODES.THESIS
                      ? ["csv", "pdf"]
                      : ["csv", "pdf", "marc21"]
                  }
                />
              </div>

              <EmptyResults />
              <Error />
              <SearchControls modelName={modelName} />
              <ResultsMultiLayout />
              <Container fluid className="search-results-footer">
                <SearchFooter />
              </Container>
              {this.renderSearchMessage()}
            </Grid.Column>
          </ResultsLoader>
        </Grid>
      </Container>
    );
  };

  renderMobileView = () => {
    const { searchMode } = this.state;

    return (
      <>
        <Container>
          <Header as="h3">
            {searchMode === SEARCH_MODES.THESIS
              ? "Theses and Dissertations"
              : "General Collection"}
          </Header>
        </Container>
        {searchMode === SEARCH_MODES.THESIS ? (
          <ThesisSearchMobile />
        ) : (
          <LiteratureSearchMobile />
        )}
      </>
    );
  };

  renderErrorState = () => {
    const { errorMessage } = this.state;

    return (
      <div style={{ padding: "2em", textAlign: "center" }}>
        <h2>Something went wrong with the Literature Search</h2>
        <p>Error: {errorMessage}</p>
        <button
          onClick={() => window.location.reload()}
          style={{
            padding: "0.5em 1em",
            backgroundColor: "#007bff",
            color: "white",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
          }}
        >
          Reload Page
        </button>
      </div>
    );
  };

  render() {
    const { hasError, refreshKey, searchMode } = this.state;
    const { overriddenSearchAppCmps } = this.props;

    if (hasError) {
      return this.renderErrorState();
    }

    try {
      const modelName =
        searchMode === SEARCH_MODES.THESIS
          ? MODEL_NAMES.SPECIAL_COLLECTIONS
          : MODEL_NAMES.LITERATURE;

      const rawInitialState =
        setReactSearchKitInitialQueryState(modelName) || {};
      const initialState = {
        ...rawInitialState,
        page: Math.max(
          1,
          Number(rawInitialState.page || rawInitialState.p || 1) || 1
        ),
        size: Math.max(1, Number(rawInitialState.size || 10) || 10),
      };

      const defaultSortingOnEmptyQueryString =
        setReactSearchKitDefaultSortingOnEmptyQueryString(
          MODEL_NAMES.LITERATURE
        );
      const urlHandler = setReactSearchKitUrlHandler(MODEL_NAMES.LITERATURE);

      const overrides = {
        ...SearchControlsOverridesMap,
        ...LiteratureSearchOverridesMap,
        ...SearchBarOverridesMap,
        ...(searchMode === SEARCH_MODES.THESIS ? ThesisSearchOverridesMap : {}),
        ...overriddenSearchAppCmps,
      };

      return (
        <Overridable id="LiteratureSearch.searchAppCmps" {...this.props}>
          <OverridableContext.Provider value={overrides}>
            <ReactSearchKit
              key={`literature-search-${refreshKey}-${searchMode}`}
              searchApi={this.getSearchApi()}
              history={history}
              urlHandlerApi={urlHandler}
              overridableId="LiteratureSearchOverridable"
              initialQueryState={initialState}
              defaultSortingOnEmptyQueryString={
                defaultSortingOnEmptyQueryString
              }
            >
              <Overridable id="LiteratureSearch.layout">
                <>
                  <Container fluid className="literature-search-container">
                    <Container>
                      <EnhancedSearchBar
                        placeholder={
                          searchMode === SEARCH_MODES.THESIS
                            ? "Search thesis and dissertations..."
                            : invenioConfig.APP.HOME_SEARCH_BAR_PLACEHOLDER
                        }
                        {...invenioConfig.APP.SEARCH_BAR_PROPS}
                        onForceRefresh={this.handleForceRefresh}
                      />
                      <div
                        style={{
                          display: "flex",
                          alignItems: "flex-start",
                          flexDirection: "row",
                          justifyContent: "space-between",
                          flexWrap: "wrap",
                          gap: "15px",
                        }}
                      >
                        <div style={{ flex: "1", minWidth: "100%" }}>
                          {this.renderAdvancedSearchToggle()}
                        </div>
                        <div style={{ width: "100%", textAlign: "right" }}>
                          <SearchGuideLink />
                        </div>
                      </div>
                    </Container>
                  </Container>
                  <SessionFiltersPersistence moduleIdentifier="literature" />
                  <Overridable id="LiteratureSearch.results.header" />

                  <Media greaterThanOrEqual="computer">
                    {this.renderDesktopView(
                      modelName,
                      initialState,
                      defaultSortingOnEmptyQueryString
                    )}
                  </Media>

                  <Media lessThan="computer">{this.renderMobileView()}</Media>
                </>
              </Overridable>
            </ReactSearchKit>
          </OverridableContext.Provider>
        </Overridable>
      );
    } catch (error) {
      return this.renderErrorState();
    }
  }
}

export default Overridable.component("LiteratureSearch", LiteratureSearch);
